import * as XLSX from "xlsx";   // Round 3a: Export Excel in the print preview
﻿import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  useDeferredValue,
} from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  InputGroup,
  InputGroupText,
  Row,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import api from "../../api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useAppDialog } from "./AppDialogProvider";
import { getMafiaIsReleaser, resolveEmployeeNames } from "./mafiaIdentity";
import { MafiaPageHeader, TableSkeleton, EmptyState } from "./_shared";
import { ColumnFilterRow } from "../../Components/Common/DashboardKit";
import MafiaTheme from "./MafiaTheme";
// Text-only report branding (owner request): brand titles + shared address.
import { BRAND_TITLES, DIR_ADDRESS_LINE } from "./dirToPdf";

const TABLE_HEIGHT = 540;
const PREVIEW_TABLE_HEIGHT = 620;
const ROW_HEIGHT = 53;
const OVERSCAN = 10;
const PRINT_PAGE_SIZE = 200;   // Batch 3.1: server page size for the deduped feed

export default function PrintPage({ onBack }) {
  const dialog = useAppDialog();


  // Round 3a (owner): "import excel" — exports the previewed table as a real
  // .xlsx (SheetJS, already a project dependency) so it opens straight in
  // Excel. Columns mirror the preview rows one-to-one.
  const exportPreviewExcel = () => {
    const data = Array.isArray(reportRows) ? reportRows : [];
    if (!data.length) return;
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "MAFIA Table");
    const part = data[0]?.PartNum || data[0]?.partNum || "table";
    XLSX.writeFile(wb, `MAFIA_${part}.xlsx`);
  };

  const [rows, setRows] = useState([]);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [printPage, setPrintPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedRowKey, setSelectedRowKey] = useState("");

  // Batch 3.1: EPO-style per-column filters (backend-driven) replace the global
  // search on the Print list. columnFilters is the live input; serverColumnFilters
  // is the debounced copy sent to the server.
  const [columnFilters, setColumnFilters] = useState({});
  const [serverColumnFilters, setServerColumnFilters] = useState({});

  const [reportRows, setReportRows] = useState([]);
  const [reportTitle, setReportTitle] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [printing, setPrinting] = useState(false);
  // Rounds 6 batch — Document Branding for the exported PDF (approved Q8).
  const [branding, setBranding] = useState("sswhite");

  const [scrollTop, setScrollTop] = useState(0);

  const searchInputRef = useRef(null);
  const previewPrintRef = useRef(null);
  const tableScrollRef = useRef(null);
  const mountedRef = useRef(true);
  const pendingScrollToKeyRef = useRef("");

  // Legacy Print.vb lines 13-19: Delete button is only shown to "releaser"
  // users, where releaser is true ONLY when myPerm = 1 or 2 (see Menu.vb).
  // Resolves to false until whoami responds, keeping the destructive button
  // safely hidden during initial load.
  const [releaser, setReleaser] = useState(false);
  useEffect(() => {
    let cancelled = false;
    getMafiaIsReleaser()
      .then((value) => { if (!cancelled) setReleaser(value === true); })
      .catch(() => { if (!cancelled) setReleaser(false); });
    return () => { cancelled = true; };
  }, []);

  const deferredSearchText = useDeferredValue(searchText);

  const rowByKey = useMemo(() => {
    const map = new Map();
    for (const row of rows) {
      map.set(row._key, row);
    }
    return map;
  }, [rows]);

  const selectedRow = useMemo(() => {
    return rowByKey.get(selectedRowKey) || null;
  }, [rowByKey, selectedRowKey]);

  const selectedPart = useMemo(() => {
    if (!selectedRow) {
      return {
        partNum: "",
        rev: 0,
        revMinor: 0,
      };
    }

    return {
      partNum: String(selectedRow.partNumber ?? ""),
      rev: Number(selectedRow.printRevision ?? 0),
      revMinor: Number(selectedRow.mafiaRevision ?? 0),
      createdBy: String(selectedRow.createdBy ?? ""),
    };
  }, [selectedRow]);

  // Batch 3.1: EPO-style per-column filters is now server-side, so the rows we
  // hold ARE the filtered page — no client global-search pass.
  const filteredRows = rows;

  // Column definitions for the filter row. ids match the mapping in getAllParts
  // (partNumber->fPartNum, etc.). "select"/timeStamp are display-only (no filter).
  const printColumns = useMemo(() => ([
    { id: "select", label: "Select", type: "none" },
    { id: "partNumber", label: "Part Number", type: "text" },
    { id: "printRevision", label: "Drawing Revision", type: "text" },
    { id: "mafiaRevision", label: "MAFIA Revision", type: "text" },
    { id: "createdBy", label: "Created By", type: "text" },
    { id: "timeStamp", label: "Time Stamp", type: "none" },
    { id: "verified", label: "Verified", type: "boolean" },
    { id: "released", label: "Released", type: "boolean" },
  ]), []);

  const totalRows = filteredRows.length;
  const totalHeight = totalRows * ROW_HEIGHT;

  const visibleRange = useMemo(() => {
    const viewportHeight = TABLE_HEIGHT;
    const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
    const endIndex = Math.min(
      totalRows,
      Math.ceil((scrollTop + viewportHeight) / ROW_HEIGHT) + OVERSCAN
    );

    return {
      startIndex,
      endIndex,
    };
  }, [scrollTop, totalRows]);

  const visibleRows = useMemo(() => {
    return filteredRows.slice(visibleRange.startIndex, visibleRange.endIndex);
  }, [filteredRows, visibleRange]);

  const topSpacerHeight = visibleRange.startIndex * ROW_HEIGHT;
  const bottomSpacerHeight = Math.max(
    0,
    totalHeight - topSpacerHeight - visibleRows.length * ROW_HEIGHT
  );

  const loadAllParts = useCallback(
    async (showSpinner = true) => {
      try {
        if (showSpinner) {
          setRefreshing(true);
        }

        const { rows: data, total } = await getAllParts({
          page: printPage,
          pageSize: PRINT_PAGE_SIZE,
          filters: serverColumnFilters,
        });
        if (!mountedRef.current) return;

        setRows(data || []);
        setRowsTotal(total || 0);

        if (selectedRowKey) {
          const stillExists = (data || []).some((row) => row._key === selectedRowKey);
          if (!stillExists) {
            setSelectedRowKey("");
          }
        }
      } catch (error) {
        console.error("Failed to load all parts:", error);
        await dialog.error("Failed to load all parts.");
      } finally {
        if (mountedRef.current) {
          setLoading(false);
          if (showSpinner) {
            setRefreshing(false);
          }
        }
      }
    },
    [selectedRowKey, dialog, printPage, serverColumnFilters]
  );

  useEffect(() => {
    mountedRef.current = true;
    loadAllParts(false);

    if (searchInputRef.current) {
      searchInputRef.current.focus();
      searchInputRef.current.select();
    }

    return () => {
      mountedRef.current = false;
    };
  }, [loadAllParts]);

  // Batch 3.1: debounce the column-filter inputs into the value sent to the
  // server, and reset to page 1 whenever they change. loadAllParts (above)
  // re-runs automatically because it depends on serverColumnFilters + printPage.
  useEffect(() => {
    const t = setTimeout(() => {
      setServerColumnFilters(columnFilters || {});
      setPrintPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [columnFilters]);

  // Batch 3.1: when the page changes, scroll the virtual list back to the top
  // so the new page starts at row 1 (otherwise the old scrollTop shows blank).
  useEffect(() => {
    if (tableScrollRef.current) tableScrollRef.current.scrollTop = 0;
    setScrollTop(0);
  }, [printPage]);

  useEffect(() => {
    const targetKey = pendingScrollToKeyRef.current;
    if (!targetKey) return;

    const index = filteredRows.findIndex((row) => row._key === targetKey);
    if (index < 0) {
      pendingScrollToKeyRef.current = "";
      return;
    }

    const container = tableScrollRef.current;
    if (container) {
      const rowTop = index * ROW_HEIGHT;
      const rowBottom = rowTop + ROW_HEIGHT;
      const viewTop = container.scrollTop;
      const viewBottom = viewTop + TABLE_HEIGHT;

      if (rowTop < viewTop) {
        container.scrollTop = rowTop;
      } else if (rowBottom > viewBottom) {
        container.scrollTop = rowBottom - TABLE_HEIGHT;
      }
    }

    pendingScrollToKeyRef.current = "";
  }, [filteredRows]);

  const handleScroll = useCallback((e) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  const selectRow = useCallback((row) => {
    if (!row) return;
    setSelectedRowKey(row._key);
  }, []);

  const handlePrint = useCallback(async () => {
    if (!selectedPart.partNum) {
      await dialog.warning("Please select a part");
      return;
    }

    try {
      setPrinting(true);

      const partData = await getPart({
        partNumber: selectedPart.partNum,
        rev: selectedPart.rev,
        revMinor: selectedPart.revMinor,
      });

      const normalizedRows = (partData || []).map((row, index) => ({
        _key: `${row.dna ?? ""}-${index}`,
        DNA: row.dna,
        DimensionType: row.dimensionType,
        Unit: row.unit,
        Nominal: row.nominal,
        Min: row.min,
        Max: row.max,
        ToleranceClass: row.toleranceClass,
        InProcess: row.inProcess,
        MethodOfRecording: row.methodOfRecording,
        SpecialInstructions: row.specialInstructions,
        WIATPNumber: row.wiatpNumber,
        Gages: row.gages,
        AdditionalInfo: row.additionalInfo,
        MomOpNumber: row.momOpNumber,
      }));

      setReportRows(normalizedRows);
      setReportTitle(
        `${selectedPart.partNum.toUpperCase()} Rev ${selectedPart.rev}-${selectedPart.revMinor}`
      );
      setShowPreview(true);
    } catch (error) {
      console.error("Failed to build print preview:", error);
      await dialog.error(error?.message || "Failed to build print preview.");
    } finally {
      if (mountedRef.current) {
        setPrinting(false);
      }
    }
  }, [dialog, selectedPart]);

  const handleSearchKeyDown = useCallback(
    (e) => {
      if (e.key !== "Enter") return;

      if (filteredRows.length > 0) {
        const firstMatch = filteredRows[0];
        setSelectedRowKey(firstMatch._key);
        pendingScrollToKeyRef.current = firstMatch._key;
      }

      if (searchInputRef.current) {
        searchInputRef.current.select();
      }
    },
    [filteredRows]
  );

  const handleDelete = useCallback(async () => {
    if (!selectedPart.partNum) return;

    const confirmed = await dialog.confirm({
      title: "Delete Part",
      message: `Are you sure you wish to delete ${selectedPart.partNum} Rev ${selectedPart.rev}-${selectedPart.revMinor}?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "error",
    });

    if (!confirmed) return;

    try {
      const success = await deleteMafiaTable({
        partnum: selectedPart.partNum,
        revMajor: selectedPart.rev,
        revMinor: selectedPart.revMinor,
      });

      if (success) {
        await dialog.success("Part successfully deleted.");
        setShowPreview(false);
        setReportRows([]);
        setReportTitle("");
        setSelectedRowKey("");
        await loadAllParts(true);

        if (searchInputRef.current) {
          searchInputRef.current.focus();
          searchInputRef.current.select();
        }
      } else {
        await dialog.error(
          "There was an error in deleting this part. Please try again or contact technical support."
        );
      }
    } catch (error) {
      console.error("Delete failed:", error);
      await dialog.error(
        "There was an error in deleting this part. Please try again or contact technical support."
      );
    }
  }, [dialog, loadAllParts, selectedPart]);

  // Round 10 (Batch 3): generate a proper PDF with jsPDF + autotable instead of
  // window.open + document.write(HTML) + browser print. The old approach used a
  // `table-layout:auto` HTML table on portrait paper, so the 14-column MAFIA
  // table got chopped/cramped (your Adobe screenshot). autotable in LANDSCAPE
  // sizes columns to the page, wraps long cells, and page-breaks cleanly across
  // many rows — the same engine EPO uses for its clean output.
  //
  // Also folds in:
  //   * Round 6: MOM OP # is the FIRST data column (was last).
  //   * Round 7: header uses "Drawing Revision" / "MAFIA Revision".
  //   * Round 8: Created By is resolved to "First Last (id)".
  const handlePopupPrint = useCallback(async () => {
    if (!reportRows || reportRows.length === 0) return;

    try {
      // Resolve the creator's display name (Round 8). Soft — falls back to raw.
      let createdByDisplay = selectedPart.createdBy || "";
      if (selectedPart.createdBy) {
        const map = await resolveEmployeeNames([selectedPart.createdBy]);
        createdByDisplay = map.get(selectedPart.createdBy) || selectedPart.createdBy;
      }

      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const M = 36;
      const INK = 33, MUTED = 110;

      // ── Header band ──────────────────────────────────────────
      // Owner request — the COMPANY header prints LARGER than the part
      // title (18pt vs 14pt), address beneath it. Text only, no logo.
      doc.setFont("helvetica", "bolditalic").setFontSize(18).setTextColor(INK);
      doc.text(BRAND_TITLES[branding] || BRAND_TITLES.sswhite, M, 28);
      doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(MUTED);
      doc.text(DIR_ADDRESS_LINE, M, 44);
      doc.setFont("helvetica", "bold").setFontSize(14).setTextColor(INK);
      doc.text(`MAFIA Table — ${selectedPart.partNum || "—"}`, M, 66);

      doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(MUTED);
      const metaLine1 =
        `Drawing Revision: ${selectedPart.rev}    ` +
        `MAFIA Revision: ${selectedPart.revMinor}    ` +
        `Rows: ${reportRows.length}`;
      doc.text(metaLine1, M, 82);
      const metaLine2 =
        `Created By: ${createdByDisplay || "—"}    ` +
        `Generated: ${new Date().toLocaleString("en-US")}`;
      doc.text(metaLine2, M, 95);

      doc.setDrawColor(INK).setLineWidth(0.8);
      doc.line(M, 104, pageW - M, 104);

      // ── Detail table — Round 6 column order (MOM OP # first) ─
      const head = [[
        "MOM OP #", "DNA", "Dimension Type", "Unit", "Nominal", "Min", "Max",
        "Tolerance Class", "In Process", "Method Of Recording",
        "Special Instructions", "WI or ATP #", "Gages", "Additional Info",
      ]];

      const s = (v) => (v == null ? "" : String(v));
      const body = reportRows.map((r) => [
        s(r.MomOpNumber), s(r.DNA), s(r.DimensionType), s(r.Unit),
        s(r.Nominal), s(r.Min), s(r.Max), s(r.ToleranceClass), s(r.InProcess),
        s(r.MethodOfRecording), s(r.SpecialInstructions), s(r.WIATPNumber),
        s(r.Gages), s(r.AdditionalInfo),
      ]);

      autoTable(doc, {
        startY: 112,
        margin: { left: M, right: M },
        head,
        body,
        theme: "grid",
        styles: {
          fontSize: 7.5, cellPadding: 3, textColor: INK,
          lineColor: [200, 200, 200], lineWidth: 0.4, valign: "top",
          overflow: "linebreak",   // wrap long cells instead of clipping
        },
        headStyles: {
          fillColor: [54, 54, 70], textColor: 255, fontStyle: "bold",
          fontSize: 7.5, halign: "left",
        },
        alternateRowStyles: { fillColor: [247, 247, 250] },
        columnStyles: {
          0: { cellWidth: 46, halign: "center" },  // MOM OP #
          1: { cellWidth: 42 },                    // DNA
          2: { cellWidth: 62 },                    // Dimension Type
          3: { cellWidth: 30, halign: "center" },  // Unit
          4: { cellWidth: 44, halign: "right" },   // Nominal
          5: { cellWidth: 40, halign: "right" },   // Min
          6: { cellWidth: 40, halign: "right" },   // Max
          7: { cellWidth: 58 },                    // Tolerance Class
          8: { cellWidth: 48 },                    // In Process
          9: { cellWidth: "auto" },                // Method Of Recording
          10: { cellWidth: "auto" },               // Special Instructions
          11: { cellWidth: 52 },                   // WI or ATP #
          12: { cellWidth: 60 },                   // Gages
          13: { cellWidth: "auto" },               // Additional Info
        },
        // Footer page numbers on every page.
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          const current = data.pageNumber;
          doc.setFont("helvetica", "normal").setFontSize(7.5).setTextColor(MUTED);
          doc.text(
            `${selectedPart.partNum} · Rev ${selectedPart.rev}-${selectedPart.revMinor} · Page ${current} of ${pageCount}`,
            pageW - M, pageH - 16, { align: "right" }
          );
        },
      });

      doc.save(
        `MAFIA_${selectedPart.partNum || "part"}_Rev_${selectedPart.rev}-${selectedPart.revMinor}.pdf`
      );
    } catch (err) {
      console.error("PDF generation failed:", err);
      await dialog.error("Could not generate the PDF. Please try again or contact technical support.");
    }
  }, [reportRows, selectedPart, dialog, branding]);

  const downloadPreviewHtml = useCallback(() => {
    const printContents = previewPrintRef.current?.innerHTML;
    if (!printContents) return;

    const html = `
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${reportTitle}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              color: #000;
            }
            h4 {
              margin: 0 0 6px 0;
            }
            .print-meta {
              margin-bottom: 14px;
              color: #555;
              font-size: 14px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              table-layout: auto;
              font-size: 12px;
            }
            th, td {
              border: 1px solid #999;
              padding: 6px 8px;
              text-align: left;
              vertical-align: top;
              white-space: normal;
              word-break: break-word;
            }
            th {
              background: #eee;
              font-weight: bold;
            }
          </style>
        </head>
        <body>
          ${printContents}
        </body>
      </html>
    `;

    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${selectedPart.partNum}_Rev_${selectedPart.rev}_${selectedPart.revMinor}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
  }, [reportTitle, selectedPart]);

  return (
    <div className="print-page mafia-screen">
      <MafiaTheme />
      <MafiaPageHeader
        icon="ri-printer-line"
        eyebrow="MAFIA Aux"
        title="Print"
        subtitle="Generate and download print previews of MAFIA parts."
        tone="default"
        actions={
          <Button
            color="light"
            onClick={onBack}
            style={{
              background: "rgba(255,255,255,0.92)",
              border: "none",
              fontWeight: 500,
            }}
          >
            <i className="ri-arrow-left-line align-bottom me-1" />
            Back to Menu
          </Button>
        }
        breadcrumb={[
          { label: "Applications", to: "/" },
          { label: "MAFIA Aux", onClick: onBack },
          { label: "Print" },
        ]}
      />

      <Row className="g-4">
        <Col xl={12}>
          <Card className="border-0 shadow-sm">
            <CardHeader className="bg-white border-0 pt-4 px-4 pb-3">
              <div className="print-toolbar-main">
                <div className="print-toolbar-main-left">
                  {/* Back moved to page header */}
                </div>

                <div className="print-toolbar-main-right">
                  {/* Batch 3.1: global search removed — use the per-column filter
                      boxes under the table headers (backend-driven, exact + fast). */}
                  <span className="text-muted small">
                    <i className="ri-filter-3-line me-1" />
                    Filter using the column boxes below
                  </span>
                </div>
              </div>

              <div className="print-toolbar-utility">
                <Button
                  color="primary"
                  className="print-action-btn print-utility-btn"
                  onClick={handlePrint}
                  disabled={printing || !selectedPart.partNum}
                >
                  <i className="ri-printer-line print-btn-icon me-1" />
                  {printing ? "Preparing..." : "Build Print Preview"}
                </Button>

                <Button
                  color="secondary"
                  className="print-action-btn print-utility-btn"
                  onClick={() => loadAllParts(true)}
                  disabled={refreshing}
                >
                  <i className="ri-refresh-line print-btn-icon me-1" />
                  {refreshing ? "Refreshing..." : "Refresh"}
                </Button>

                {releaser && (
                  <Button
                    color="danger"
                    outline
                    className="print-action-btn print-utility-btn"
                    onClick={handleDelete}
                    disabled={!selectedPart.partNum}
                  >
                    <i className="ri-delete-bin-6-line print-btn-icon me-1" />
                    Delete Part
                  </Button>
                )}

                <Badge color="light" className="print-queue-badge">
                  {(() => {
                    const totalPages = Math.max(1, Math.ceil((rowsTotal || 0) / PRINT_PAGE_SIZE));
                    return `Parts: ${rowsTotal} (page ${printPage} of ${totalPages})`;
                  })()}
                </Badge>

                <div className="print-info-pill">
                  <span className="print-info-label">Selected Part</span>
                  <span
                    className="print-info-value"
                    title={selectedRow?.partNumber || "No selection"}
                  >
                    {selectedRow?.partNumber || "No selection"}
                  </span>
                </div>

                <div className="print-info-pill">
                  <span className="print-info-label">Revision</span>
                  <span className="print-info-value">
                    {selectedRow
                      ? `${selectedRow.printRevision ?? 0}.${selectedRow.mafiaRevision ?? 0}`
                      : "—"}
                  </span>
                </div>

                <div className="print-info-pill">
                  <span className="print-info-label">Preview State</span>
                  <span className="print-info-value">
                    {showPreview ? "Ready" : "Not Generated"}
                  </span>
                </div>
              </div>
            </CardHeader>

            <CardBody className="p-4">
              <div className="print-table-shell">
                <div
                  ref={tableScrollRef}
                  className="print-table-scroll"
                  onScroll={handleScroll}
                >
                  <Table responsive={false} className="align-middle mb-0 print-table">
                    <thead className="table-light">
                      <tr>
                        <th className="print-col-select">Select</th>
                        <th>Part Number</th>
                        <th>Drawing Revision</th>
                        <th>MAFIA Revision</th>
                        <th>Created By</th>
                        <th>Time Stamp</th>
                        <th>Verified</th>
                        <th>Released</th>
                      </tr>
                      {/* Batch 3.1: per-column filter row (replaces global search). */}
                      <ColumnFilterRow
                        columns={printColumns}
                        filters={columnFilters}
                        onChange={setColumnFilters}
                        live
                      />
                    </thead>
                    <tbody>
                      {loading ? (
                        <TableSkeleton rows={6} columns={8} />
                      ) : filteredRows.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="p-0">
                            <EmptyState
                              icon="ri-printer-line"
                              title="No parts to print"
                              message={
                                Object.values(serverColumnFilters || {}).some((v) => String(v || "").trim() !== "")
                                  ? "No parts match the current column filters. Try clearing or adjusting them."
                                  : "Add parts to MAFIA to make them available for print."
                              }
                              tone="secondary"
                            />
                          </td>
                        </tr>
                      ) : (
                        <>
                          {topSpacerHeight > 0 && (
                            <tr style={{ height: `${topSpacerHeight}px` }}>
                              <td colSpan={8} style={{ padding: 0, border: 0 }} />
                            </tr>
                          )}

                          {visibleRows.map((row) => {
                            const isSelected = row._key === selectedRowKey;

                            return (
                              <tr
                                key={row._key}
                                tabIndex={0}
                                onClick={() => selectRow(row)}
                                className={`print-data-row ${isSelected ? "selected" : ""}`}
                                style={{ height: `${ROW_HEIGHT}px` }}
                              >
                                <td className="print-col-select">
                                  <Input
                                    type="radio"
                                    checked={isSelected}
                                    onChange={() => selectRow(row)}
                                    name="selected-print-row"
                                  />
                                </td>
                                <td>
                                  <div className="fw-semibold text-dark">{row.partNumber}</div>
                                </td>
                                <td>{row.printRevision}</td>
                                <td>{row.mafiaRevision}</td>
                                <td>{row.createdBy}</td>
                                <td>{row.timeStampDisplay}</td>
                                <td>
                                  <StatusBadge value={row.verified} />
                                </td>
                                <td>
                                  <StatusBadge value={row.released} />
                                </td>
                              </tr>
                            );
                          })}

                          {bottomSpacerHeight > 0 && (
                            <tr style={{ height: `${bottomSpacerHeight}px` }}>
                              <td colSpan={8} style={{ padding: 0, border: 0 }} />
                            </tr>
                          )}
                        </>
                      )}
                    </tbody>
                  </Table>
                </div>

                {/* Batch 3.1: pagination for the server-paged deduped feed. */}
                {(() => {
                  const totalPages = Math.max(1, Math.ceil((rowsTotal || 0) / PRINT_PAGE_SIZE));
                  if (totalPages <= 1) return null;
                  return (
                    <div className="d-flex align-items-center justify-content-between mt-3">
                      <span className="text-muted small">
                        Showing {rows.length === 0 ? 0 : (printPage - 1) * PRINT_PAGE_SIZE + 1}
                        {" – "}
                        {(printPage - 1) * PRINT_PAGE_SIZE + rows.length} of {rowsTotal}
                      </span>
                      <div className="d-flex gap-2">
                        <Button
                          size="sm"
                          color="light"
                          disabled={printPage <= 1 || refreshing}
                          onClick={() => setPrintPage((p) => Math.max(1, p - 1))}
                        >
                          <i className="ri-arrow-left-s-line" /> Prev
                        </Button>
                        <Button
                          size="sm"
                          color="light"
                          disabled={printPage >= totalPages || refreshing}
                          onClick={() => setPrintPage((p) => Math.min(totalPages, p + 1))}
                        >
                          Next <i className="ri-arrow-right-s-line" />
                        </Button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal
        isOpen={showPreview}
        toggle={() => setShowPreview(false)}
        size="xl"
        centered
        scrollable
        className="print-preview-modal"
      >
        <ModalHeader toggle={() => setShowPreview(false)}>
          Print Preview - {reportTitle}
        </ModalHeader>

        <ModalBody>
          {/* Rounds 6 batch — Document Branding: prints logo + shared address
              on the PDF header (approved Q8). */}
          <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
            <span className="fw-semibold small text-muted">Document Branding:</span>
            {[
              { value: "sswhite", label: "SS White" },
              { value: "shukla", label: "Shukla Medical" },
              { value: "sss", label: "Shukla Surgical Support" },
            ].map((b) => (
              <label
                key={b.value}
                className="d-inline-flex align-items-center gap-1 mb-0 small"
                style={{ cursor: "pointer" }}
              >
                <input
                  type="radio"
                  className="form-check-input"
                  name="printBranding"
                  checked={branding === b.value}
                  onChange={() => setBranding(b.value)}
                />
                <span>{b.label}</span>
              </label>
            ))}
          </div>

          <div className="d-flex flex-wrap gap-2 mb-3">
            <Button color="dark" className="print-action-btn" onClick={handlePopupPrint}>
              <i className="ri-file-pdf-line print-btn-icon me-1" />
              Download PDF
            </Button>

            <Button color="secondary" className="print-action-btn" onClick={downloadPreviewHtml}>
              <i className="ri-download-line print-btn-icon me-1" />
              Save Table File
            </Button>

            <Button color="success" className="print-action-btn" onClick={exportPreviewExcel}>
              <i className="ri-file-excel-2-line print-btn-icon me-1" />
              Export Excel
            </Button>

            <Button
              color="light"
              className="print-action-btn border"
              onClick={() => setShowPreview(false)}
            >
              <i className="ri-close-line print-btn-icon me-1" />
              Close
            </Button>
          </div>

          <div ref={previewPrintRef} className="popup-print-area">
            <div className="mb-3">
              <h4 className="mb-1">{reportTitle}</h4>
              <div className="print-meta">
                Part: {selectedPart.partNum} | Rev: {selectedPart.rev}.{selectedPart.revMinor}
              </div>
            </div>

            <div className="print-table-shell">
              <div
                className="print-table-scroll print-table-scroll-preview"
                style={{ maxHeight: `${PREVIEW_TABLE_HEIGHT}px` }}
              >
                <Table
                  responsive={false}
                  className="align-middle mb-0 print-table print-preview-table"
                >
                  <thead className="table-light">
                    <tr>
                      {/* Round 6 (Batch 3): MOM OP # first, matching the PDF and grids. */}
                      <th>MOM OP #</th>
                      <th>DNA</th>
                      <th>Dimension Type</th>
                      <th>Unit</th>
                      <th>Nominal</th>
                      <th>Min</th>
                      <th>Max</th>
                      <th>Tolerance Class</th>
                      <th>In Process</th>
                      <th>Method Of Recording</th>
                      <th>Special Instructions</th>
                      <th>WI or ATP Number</th>
                      <th>Gages</th>
                      <th>Additional Info</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportRows.length === 0 ? (
                      <tr>
                        <td colSpan={14} className="text-center py-5 text-muted">
                          No report data found.
                        </td>
                      </tr>
                    ) : (
                      reportRows.map((row) => (
                        <tr key={row._key} className="print-data-row">
                          <td>{row.MomOpNumber}</td>
                          <td>{row.DNA}</td>
                          <td>{row.DimensionType}</td>
                          <td>{row.Unit}</td>
                          <td>{row.Nominal}</td>
                          <td>{row.Min}</td>
                          <td>{row.Max}</td>
                          <td>{row.ToleranceClass}</td>
                          <td>{row.InProcess}</td>
                          <td>{row.MethodOfRecording}</td>
                          <td>{row.SpecialInstructions}</td>
                          <td>{row.WIATPNumber}</td>
                          <td>{row.Gages}</td>
                          <td>{row.AdditionalInfo}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>

      <style>{`
        .print-toolbar-main {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: wrap;
          margin-bottom: 0.9rem;
        }

        .print-toolbar-main-left {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .print-toolbar-main-right {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          flex: 0 0 360px;
          min-width: 280px;
        }

        .print-toolbar-utility {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          padding-top: 0.25rem;
        }

        .print-search-group {
          width: 100%;
        }

        .print-action-btn {
            white-space: nowrap;
          }
          /* Make disabled primary buttons obviously disabled (not faded primary) */
          .print-action-btn:disabled,
          .print-action-btn.disabled {
            background-color: #e9ebec !important;
            border-color: #e9ebec !important;
            color: #878a99 !important;
            opacity: 1 !important;
          }


        .print-utility-btn {
            min-width: 110px;
          }

        .print-btn-icon {
          font-size: 0.95rem;
        }

        .btn-soft-dark {
          background: #eff2f7;
          color: #212529;
          border: 1px solid #e9ebec;
        }

        .btn-soft-dark:hover,
        .btn-soft-dark:focus {
          background: #e9ebec;
          color: #212529;
          border-color: #dee2e6;
        }

        .print-queue-badge {
            display: inline-flex;
            align-items: center;
            min-height: 32px;
            border: 1px solid #e9ebec;
            padding: 0.3rem 0.65rem;
            border-radius: 0.375rem;
            font-size: 0.8125rem;
            font-weight: 600;
            color: #495057;
            background: #f3f6f9;
          }

        .print-info-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            min-height: 32px;
            padding: 0.3rem 0.65rem;
            border: 1px solid #e9ebec;
            border-radius: 0.375rem;
            background: #f3f6f9;
            max-width: 300px;
          }

        .print-info-label {
            font-size: 0.7rem;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            color: #878a99;
            white-space: nowrap;
          }

        .print-info-value {
            font-size: 0.8125rem;
            font-weight: 600;
            color: #495057;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

        .print-table-shell {
          border: 1px solid #e9ebec;
          border-radius: 1rem;
          background: #fff;
        }

        .print-table-scroll {
          max-height: ${TABLE_HEIGHT}px;
          overflow: auto;
          will-change: transform;
        }

        .print-table-scroll-preview {
          /* The MAFIA preview is the content the user wants to read end-to-end.
             Let the page scroll vertically; keep horizontal scroll for the
             wide preview table. */
          max-height: none;
          overflow-x: auto;
          overflow-y: visible;
        }

        .print-table {
          min-width: 1120px;
        }

        .print-preview-table {
          min-width: 1800px;
        }

        .print-table thead th {
          z-index: 2;
          background: #f3f6f9;
          white-space: nowrap;
        }

        .print-table tbody td {
          padding: 0.9rem 0.9rem;
          border-top: 1px solid #eef2f7;
          color: #495057;
          vertical-align: middle;
          white-space: nowrap;
        }

        .popup-print-area .print-table tbody td {
          vertical-align: top;
        }

        .print-col-select {
          width: 72px;
          text-align: center;
        }

        .print-data-row {
          transition: background-color 0.18s ease, box-shadow 0.18s ease;
          cursor: pointer;
        }

        .print-data-row:nth-of-type(even) {
          background: #fbfcfe;
        }

        .print-data-row:hover {
          background: #f5f9ff;
        }

        .print-data-row.selected {
          background: rgba(13, 110, 253, 0.08);
          box-shadow: inset 4px 0 0 #0d6efd;
        }

        .print-status {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.35rem 0.65rem;
          border-radius: 999px;
          font-size: 0.78rem;
          font-weight: 700;
        }

        .print-status.yes {
          background: rgba(25, 135, 84, 0.12);
          color: #198754;
        }

        .print-status.no {
          background: rgba(220, 53, 69, 0.1);
          color: #dc3545;
        }

        .print-status.na {
          background: rgba(100, 116, 139, 0.12);
          color: #878a99;
        }

        .print-preview-modal .modal-dialog {
          max-width: 95vw;
        }

        .print-preview-modal .modal-body {
          padding: 1rem 1rem 1.25rem;
        }

        .print-meta {
          color: #878a99;
          font-size: 0.95rem;
          font-weight: 500;
        }

        @media (max-width: 1199.98px) {
          .print-toolbar-main {
            flex-direction: column;
            align-items: stretch;
          }

          .print-toolbar-main-right {
            flex: 1 1 auto;
            min-width: 100%;
          }
        }

        @media (max-width: 991.98px) {
          .print-table-scroll {
            max-height: 460px;
          }

          .print-info-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            min-height: 32px;
            padding: 0.3rem 0.65rem;
            border: 1px solid #e9ebec;
            border-radius: 0.375rem;
            background: #f3f6f9;
            max-width: 300px;
          }

          .print-toolbar-utility {
            align-items: stretch;
          }
        }
      `}</style>
    </div>
  );
}

function StatusBadge({ value }) {
  if (value === true) {
    return (
      <span className="print-status yes">
        <i className="ri-checkbox-circle-fill" /> Yes
      </span>
    );
  }

  if (value === false) {
    return (
      <span className="print-status no">
        <i className="ri-close-circle-fill" /> No
      </span>
    );
  }

  return <span className="print-status na">—</span>;
}

function getRowKey(row) {
  return `${row.partNumber ?? ""}-${row.printRevision ?? 0}-${row.mafiaRevision ?? 0}`;
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function normalizePartRow(item, index = 0) {
  const partNumber = item.partNumber ?? "";
  const printRevision = item.printRevision ?? 0;
  const mafiaRevision = item.mafiaRevision ?? 0;
  const createdBy = item.createdBy ?? "";
  const timeStamp = item.timeStamp ?? "";
  const timeStampDisplay = formatDateTime(timeStamp);

  const normalized = {
    partNumber,
    printRevision,
    mafiaRevision,
    createdBy,
    timeStamp,
    timeStampDisplay,
    verified: item.verified,
    released: item.released,
  };

  // Fix (Batch 3.1): the old key was partNumber-printRev-mafiaRev, which is
  // NOT unique — parts with the same value (e.g. many "-" rows) collided on one
  // key, so selecting any row selected them all / none. Appending the array
  // index guarantees a unique, stable key per rendered row.
  normalized._key = `${getRowKey(normalized)}#${index}`;
  normalized._search = [
    String(partNumber),
    String(printRevision),
    String(mafiaRevision),
    String(createdBy),
    String(timeStampDisplay),
  ]
    .join("|")
    .toUpperCase();

  return normalized;
}

// Fix (Batch 3.1): Print now uses the SAME deduped + indexed feed as the All
// Parts screen (`edit-parts`) instead of the raw `all-parts` scan. `all-parts`
// returned every raw revision row ordered by PartNum, so the "-" part's many
// revisions dominated the top 1000 (the identical rows you saw) AND it wasn't
// covered by the performance index (hence "keeps loading"). `edit-parts`
// returns ONE latest row per part, fast, via IX_MafiaTables_LatestVersion.
async function getAllParts({ page = 1, pageSize = 200, filters } = {}) {
  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("pageSize", String(pageSize));
  if (filters) {
    const m = {
      partNumber: "fPartNum",
      printRevision: "fRev",
      mafiaRevision: "fRevMinor",
      createdBy: "fCreatedBy",
      verified: "fVerified",
      released: "fReleased",
    };
    for (const [colId, param] of Object.entries(m)) {
      const v = filters[colId];
      if (v != null && String(v).trim() !== "") params.set(param, String(v).trim());
    }
  }
  const response = await api.get(`mafia/edit-parts?${params.toString()}`);
  const data = response.data;
  const items = (data && Array.isArray(data.items)) ? data.items : (Array.isArray(data) ? data : []);
  const total = (data && typeof data.totalCount === "number") ? data.totalCount : items.length;
  return { rows: items.map((it, i) => normalizePartRow(it, (page - 1) * pageSize + i)), total };
}

async function getPart(request) {
  const response = await api.post("mafia/part", {
    partNumber: request.partNumber,
    rev: request.rev,
    revMinor: request.revMinor,
  });

  return response.data;
}

async function deleteMafiaTable(request) {
  const response = await api.delete("mafia/deletemafiatable", {
    params: {
      partnum: request.partnum,
      revMajor: request.revMajor,
      revMinor: request.revMinor,
    },
  });

  return response.data;
}